// AI トーク（「この未来について話す」）の画面の仕組み。Web とモバイルで共通。
// 方針：本人が送ったときだけ動く／会話はカードごと／会話でカードは変わらない／
// 使えない（無効・上限・混雑）ときは、理由と、いつ使えるかを示す。手入力のカード編集は常に使える。
import type { AssistQuota, TalkHistoryMessage } from "../../api/futureBoard";

// サーバーは 4,000 文字まで受けるが、画面では短く保つ（LLM に渡す量を抑える）。
export const TALK_MAX_CHARS = 1000;

export type TalkRole = "user" | "assistant";

export interface TalkMessage {
  id: string;
  role: TalkRole;
  content: string;
  /** 返答を待っている（届いた分だけ content に入る） */
  pending?: boolean;
}

const length = (value: string) => [...value.trim()].length;

// --- 使えるか ---

export type TalkAvailability = "unknown" | "disabled" | "ready" | "exhausted";

export function talkAvailability(quota: AssistQuota | null): TalkAvailability {
  if (!quota) return "unknown";
  if (!quota.enabled) return "disabled";
  return (quota.remaining ?? 0) <= 0 ? "exhausted" : "ready";
}

export type TalkBlocker = "empty" | "too_long" | "exhausted" | "busy";

// 送れるか。送れないなら理由（画面は、押せない理由を示す）。
export function talkBlocker(text: string, options: { availability: TalkAvailability; busy: boolean }): TalkBlocker | null {
  if (options.busy) return "busy";
  if (options.availability === "exhausted") return "exhausted";
  const size = length(text);
  if (size === 0) return "empty";
  if (size > TALK_MAX_CHARS) return "too_long";
  return null;
}

export const talkBlockerKey = (blocker: TalkBlocker): string => `futureBoard.talk.blocker.${blocker}`;

// --- 会話の状態 ---

export interface TalkState {
  messages: TalkMessage[];
  phase: "idle" | "sending";
  messageKey: string | null;
  resetsAt: string | null;
  /** 送れなかった文章。入力欄に戻す（書いた内容を失わない） */
  restore: string | null;
}

export const initialTalkState: TalkState = { messages: [], phase: "idle", messageKey: null, resetsAt: null, restore: null };

export type TalkAction =
  | { type: "loaded"; messages: TalkMessage[] }
  | { type: "send"; text: string; userId: string; replyId: string }
  | { type: "token"; replyId: string; text: string }
  | { type: "done"; replyId: string; text: string }
  | { type: "failed"; replyId: string; userId: string; messageKey: string; resetsAt?: string | null }
  | { type: "dismiss" }
  | { type: "clearRestore" };

export function talkReducer(state: TalkState, action: TalkAction): TalkState {
  switch (action.type) {
    case "loaded":
      // 送信中に読み込みが終わっても、いまの会話は消さない
      return state.phase === "sending" ? state : { ...state, messages: action.messages };
    case "send": {
      const text = action.text.trim();
      if (!text || state.phase === "sending") return state;
      return {
        ...state, phase: "sending", messageKey: null, resetsAt: null, restore: null,
        messages: [...state.messages, { id: action.userId, role: "user", content: text }, { id: action.replyId, role: "assistant", content: "", pending: true }],
      };
    }
    case "token":
      return { ...state, messages: state.messages.map(message => (message.id === action.replyId ? { ...message, content: action.text } : message)) };
    case "done":
      return { ...state, phase: "idle", messages: state.messages.map(message => (message.id === action.replyId ? { ...message, content: action.text, pending: false } : message)) };
    case "failed": {
      // 返答が出なかった送信は、会話に残さない（サーバーにも保存されない）。送った文章は、入力欄に戻す。
      const sent = state.messages.find(message => message.id === action.userId)?.content ?? null;
      return {
        ...state, phase: "idle", messageKey: action.messageKey, resetsAt: action.resetsAt ?? null, restore: sent,
        messages: state.messages.filter(message => message.id !== action.userId && message.id !== action.replyId),
      };
    }
    case "dismiss":
      return { ...state, messageKey: null, resetsAt: null };
    case "clearRestore":
      return { ...state, restore: null };
    default:
      return state;
  }
}

// サーバーの履歴を、画面の会話にする
export function fromHistory(messages: readonly TalkHistoryMessage[]): TalkMessage[] {
  return messages
    .filter(message => (message.role === "user" || message.role === "assistant") && message.content)
    .map(message => ({ id: `history-${message.id}`, role: message.role, content: message.content }));
}

// --- 「今日の行動を考える」への入口 ---

// 画面の遷移で渡すカードの ID（クエリ文字列など）。正の整数でなければ null（通常の開き方のまま）。
export function parseFutureCardParam(value: unknown): number | null {
  const text = Array.isArray(value) ? value[0] : value;
  if (typeof text !== "string" && typeof text !== "number") return null;
  const id = Number(text);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}
